import { authClient } from '@/auth/client';
import type { SessionUser } from '@/auth/types';
import { DashboardSidebar } from '@/components/layout/dashboard-sidebar';
import { Footer } from '@/components/layout/footer';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { getLocaleDir } from '@/lib/locale';
import { Outlet } from '@tanstack/react-router';
import type { ReactNode } from 'react';

interface SidebarLayoutProps {
  children: ReactNode;
}

/** Shared sidebar chrome for every non-auth, non-404 page. */
export function SidebarLayout({ children }: SidebarLayoutProps) {
  const { data: session } = authClient.useSession();

  return (
    <SidebarLayoutShell user={session?.user ?? null}>
      {children}
    </SidebarLayoutShell>
  );
}

function SidebarLayoutShell({
  children,
  user,
}: SidebarLayoutProps & { user: SessionUser | null }) {
  const dir = getLocaleDir();

  return (
    <TooltipProvider>
      <SidebarProvider
        className="h-svh flex overflow-hidden"
        dir={dir}
        style={
          {
            '--sidebar-width': 'calc(var(--spacing) * 72)',
            '--header-height': 'calc(var(--spacing) * 12)',
          } as React.CSSProperties
        }
      >
        <DashboardSidebar user={user} variant="inset" />
        <SidebarInset>
          <div
            className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto"
            data-scroll-restoration-id="app-content"
          >
            <div className="min-h-full shrink-0">{children}</div>
            <Footer className="shrink-0" />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}

/** Convenience wrapper for route files that just need SidebarLayout + Outlet. */
export function SidebarLayoutPage() {
  return (
    <SidebarLayout>
      <Outlet />
    </SidebarLayout>
  );
}
